import { useState, useCallback, useMemo } from 'react';
import type { ProxyTypeEnum } from 'venn-api';
import { useExtrapolateToggle } from '../components/ExtrapolationToggle';
import type { SelectedProxy } from '../utils';
import type { FundToBulkProxy, FundToProxy } from '../../types';
import { type UseProxyPickerStateOptions, ProxyPickerStateResult, type LegacyProxyPickerStateResult } from './types';
import { type LayeredDetailedProxyMetadata, getFirstProxy } from '../../../../hooks/useProxyDataQuery';

function getUniqueProxy(
  investments: (FundToProxy | FundToBulkProxy)[],
  proxyData: Record<string, LayeredDetailedProxyMetadata>,
): (SelectedProxy & { proxyType: ProxyTypeEnum }) | null {
  const firstProxies = investments[0] ? proxyData[investments[0].id] : undefined;
  const proxyInfo = getFirstProxy(firstProxies ?? ([] as const));
  const id = proxyInfo?.proxyId;
  const name = proxyInfo?.proxyName;
  const proxyType = proxyInfo?.proxyType;
  return id &&
    name &&
    proxyType &&
    investments.every((investment) => {
      const investmentProxy = getFirstProxy(proxyData[investment.id] ?? ([] as const));
      return investmentProxy?.proxyId === id && investmentProxy?.proxyType === proxyType;
    })
    ? { id, name, proxyType }
    : null;
}

export function useLegacyProxyState({
  investments,
  returnsProxyDataByFund,
}: UseProxyPickerStateOptions): LegacyProxyPickerStateResult {
  const hasSingleProxy = investments.length === 1;
  const uniqueProxy = useMemo(
    () => getUniqueProxy(investments, returnsProxyDataByFund),
    [investments, returnsProxyDataByFund],
  );

  const [selectedProxyType, setSelectedProxyType] = useState<ProxyTypeEnum | undefined>(() => uniqueProxy?.proxyType);
  const [selectedProxy, setSelectedProxy] = useState<SelectedProxy | null>(() => uniqueProxy);
  const [numLags, setNumLags] = useState(
    () =>
      (hasSingleProxy && investments[0]
        ? getFirstProxy(returnsProxyDataByFund[investments[0].id] ?? ([] as const))?.numLags
        : 0) || 0,
  );

  const { extrapolationToggleState, shouldExtrapolate, setIsExtrapolated } = useExtrapolateToggle(
    investments,
    selectedProxyType,
    returnsProxyDataByFund,
  );

  const setProxyType = useCallback((type: ProxyTypeEnum) => {
    setSelectedProxyType(type);
  }, []);

  const setProxy = useCallback((proxy: SelectedProxy | null) => {
    setSelectedProxy(proxy);
  }, []);

  const setLags = useCallback((lags: number) => {
    setNumLags(lags);
  }, []);

  const setExtrapolate = useCallback(
    (extrapolate: boolean) => {
      setIsExtrapolated(extrapolate);
    },
    [setIsExtrapolated],
  );

  return ProxyPickerStateResult.Legacy({
    state: {
      selectedProxyType,
      selectedProxy,
      numLags,
      shouldExtrapolate,
      extrapolationToggleState,
    },
    mutations: {
      setProxyType,
      setProxy,
      setNumLags: setLags,
      setExtrapolate,
    },
  });
}
